import { RECENT_LIMIT, RecentRef } from '@myrewards/contracts';

// Recent in Search & jump (SHELL1, APP-085): what this person opened lately, newest first. A
// per-viewer convenience kept in this browser, like the header overrides: it may be missing
// (private window, cleared site data) and then the palette simply starts with Jump to.
//
// Privacy, on a machine people share:
// - Only kinds and ids are kept (`award:<id>`, `page:/insights/team`), never a name or a title.
// - It belongs to one person. Another person's list is dropped the moment someone else is
//   signed in here, and the whole list goes at sign-out.
// - It's re-checked every time it's shown (server scopes for people and awards, the route tree's
//   roles for pages), so it only ever offers what the person may still open.
// - Nothing is kept or shown while previewing.

const STORAGE_KEY = 'mrv2.recent';

/**
 * A page by its address (`page:/insights/team`), or a person or award by id (a `RecentRef`:
 * `person:`, `award:` or `reward:` and a UUID). Checked as it's read back from storage.
 */
export type RecentEntry = string;

interface Stored {
  personId: string;
  refs: RecentEntry[];
}

const PAGE = /^page:\/[^\s?#]{0,200}$/;

function isEntry(value: unknown): value is RecentEntry {
  return typeof value === 'string' && (PAGE.test(value) || RecentRef.safeParse(value).success);
}

function read(): Stored | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { personId, refs } = parsed as Partial<Stored>;
    if (typeof personId !== 'string' || !Array.isArray(refs)) return null;
    return { personId, refs: refs.filter(isEntry).slice(0, RECENT_LIMIT) };
  } catch {
    return null;
  }
}

function write(stored: Stored): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Storage is full or blocked: Recent just doesn't remember this one.
  }
}

/** This person's recent opens, newest first; empty when there are none or storage isn't there. */
export function readRecent(personId: string): RecentEntry[] {
  const stored = read();
  return stored?.personId === personId ? stored.refs : [];
}

/** Puts this open first (once), keeping the newest few. Drops anyone else's list. */
export function rememberOpen(personId: string, ref: RecentEntry): void {
  const refs = [ref, ...readRecent(personId).filter((r) => r !== ref)].slice(0, RECENT_LIMIT);
  write({ personId, refs });
}

/** Removes opens the person may no longer open, keeping anything added meanwhile. */
export function forgetRecent(personId: string, gone: readonly RecentEntry[]): void {
  if (gone.length === 0) return;
  const stored = read();
  if (stored?.personId !== personId) return;
  write({ personId, refs: stored.refs.filter((r) => !gone.includes(r)) });
}

/** Someone else is signed in here now: their predecessor's list goes. */
export function claimRecent(personId: string): void {
  const stored = read();
  if (stored !== null && stored.personId !== personId) clearRecent();
}

/** Sign-out: nothing of anyone's Recent stays in this browser. */
export function clearRecent(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Blocked storage held nothing to clear.
  }
}

const ID = '[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}';
const OPENS: ReadonlyArray<[RegExp, 'award' | 'reward' | 'person']> = [
  [new RegExp(`^/awards/(${ID})$`), 'award'],
  [new RegExp(`^/my-rewards/(${ID})$`), 'reward'],
  [new RegExp(`^/(?:admin|settings/system)/people/(${ID})$`), 'person'],
];

/**
 * What an address counts as in Recent, if anything: an award record, an award in My rewards, a
 * person (their Admin record, or Recognize with them), or a page search lists. Home isn't one;
 * it's always a click away.
 */
export function recentEntryFor(
  pathname: string,
  search: string,
  pageHrefs: ReadonlySet<string>,
): RecentEntry | null {
  for (const [pattern, kind] of OPENS) {
    const id = pattern.exec(pathname)?.[1];
    if (id !== undefined) return `${kind}:${id}`;
  }
  if (pathname === '/recognize') {
    const person = new URLSearchParams(search).get('person');
    const ref = `person:${person ?? ''}`;
    if (person !== null && RecentRef.safeParse(ref).success) return ref;
  }
  if (pathname !== '/' && pageHrefs.has(pathname)) return `page:${pathname}`;
  return null;
}
